"use client";

import { AnimatePresence, motion } from "motion/react";
import Link from "next/link";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

type NavLink = {
  label: string;
  href: string;
};

// Clearance for the fixed header when scrolling to a section, plus the award
// announcement bar (features/blog/technology-innovation-award) while it shows above the header.
const HEADER_SCROLL_OFFSET = 100;

const getScrollOffset = () =>
  HEADER_SCROLL_OFFSET +
  (document.querySelector("[data-award-announcement-bar]")?.getBoundingClientRect().height ?? 0);

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Scroll-driven nav background overlay. Sits as the first child inside the
 * server-rendered <header> with `absolute inset-0` so it covers the bar.
 *
 * On a cross-page anchor landing (e.g. /pricing → /<lang>#features) the
 * browser scrolls to the fragment before the header hydrates, so the very
 * first client commit must paint the opaque "state 2" background without
 * fading in. We suppress the opacity transition for that first commit and
 * enable it on the next animation frame, so subsequent scroll-driven changes
 * still animate at 150ms.
 */
export function HeaderBackground() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [transitionsEnabled, setTransitionsEnabled] = useState(false);

  useIsomorphicLayoutEffect(() => {
    setIsScrolled(window.scrollY > 1);
    const raf = requestAnimationFrame(() => setTransitionsEnabled(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const sync = () => setIsScrolled(window.scrollY > 1);
    window.addEventListener("scroll", sync, { passive: true });
    return () => window.removeEventListener("scroll", sync);
  }, []);

  return (
    <div
      aria-hidden="true"
      className={`border-mocha/10 bg-champagne-white/90 pointer-events-none absolute inset-0 border-b backdrop-blur-2xl ${
        transitionsEnabled ? "transition-opacity duration-150" : ""
      } ${isScrolled ? "opacity-100" : "opacity-0"}`}
      data-testid="prelaunch-header-background"
    />
  );
}

/**
 * Smooth-scroll handler attached to the server-rendered desktop nav anchors
 * (matched via `data-prelaunch-nav`). Only intercepts clicks when the user is
 * already on the home page — on subpages the bare `<a href="/<lang>#fragment">`
 * navigates normally so the browser scrolls to the section after the home
 * route mounts. Renders nothing visible.
 */
export function NavScrollHandlers({ homePath }: { homePath: string }) {
  useEffect(() => {
    const cleanups: Array<() => void> = [];
    const anchors = document.querySelectorAll<HTMLAnchorElement>("a[data-prelaunch-nav]");
    anchors.forEach((anchor) => {
      const handler = (event: MouseEvent) => {
        const currentPath = window.location.pathname.replace(/\/$/, "");
        const normalizedHome = homePath.replace(/\/$/, "");
        if (currentPath !== normalizedHome) return;
        const href = anchor.getAttribute("href") ?? "";
        const fragment = href.includes("#") ? href.split("#")[1] : "";
        if (!fragment) return;
        const el = document.getElementById(fragment);
        if (!el) return;
        event.preventDefault();
        const headerOffset = getScrollOffset();
        const elementPosition = el.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - headerOffset,
          behavior: "auto",
        });
      };
      anchor.addEventListener("click", handler);
      cleanups.push(() => anchor.removeEventListener("click", handler));
    });
    return () => {
      for (const cleanup of cleanups) cleanup();
    };
  }, [homePath]);

  return null;
}

type HeaderMobileMenuProps = {
  navLinks: NavLink[];
  bookDemoLabel: string;
  ariaToggleMenu: string;
  ariaCloseMenu: string;
};

/**
 * Mobile hamburger toggle + full-screen drawer. Renders both the toggle button
 * (lives in the right-side header slot) and the drawer (fixed, rendered as a
 * sibling).
 */
export function HeaderMobileMenu({
  navLinks,
  bookDemoLabel,
  ariaToggleMenu,
  ariaCloseMenu,
}: HeaderMobileMenuProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  // The drawer is portalled to <body> to escape the header's transformed
  // ancestors, which would otherwise create a containing block and clip the
  // `position: fixed inset-0` drawer to the header bar.
  useEffect(() => setIsMounted(true), []);

  useEffect(() => {
    if (!isMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Body class lets prelaunch.css restyle the (server-rendered) header
    // contents — e.g. invert the logo to read on the espresso drawer.
    document.body.classList.add("prelaunch-menu-open");

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    const handleResize = () => {
      // Matches the `lg:` breakpoint that surfaces the desktop nav in
      // Header.tsx. Auto-close the drawer once the desktop layout takes over.
      if (window.innerWidth >= 1024) setIsMenuOpen(false);
    };

    window.addEventListener("keydown", handleKey);
    window.addEventListener("resize", handleResize);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("prelaunch-menu-open");
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMenuOpen]);

  const handleMobileNavClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      // Same-page fragment links (e.g. /<lang>#features when already on the
      // home page) get the smooth-scroll branch with offset for the sticky
      // header. Cross-page links (e.g. Pricing, Log In, or anchors clicked
      // from a subpage) fall through to native browser navigation.
      const fragmentMatch = href.match(/^([^#]*)#(.+)$/);
      if (!fragmentMatch) {
        closeMenu();
        return;
      }
      const [, hrefPath, fragment] = fragmentMatch;
      const currentPath = window.location.pathname.replace(/\/$/, "");
      const targetPath = (hrefPath ?? "").replace(/\/$/, "");
      const samePage = targetPath === "" || targetPath === currentPath;
      if (!samePage) {
        closeMenu();
        return;
      }
      const el = document.getElementById(fragment);
      if (!el) {
        closeMenu();
        return;
      }
      e.preventDefault();
      closeMenu();
      const headerOffset = getScrollOffset();
      const elementPosition = el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - headerOffset, behavior: "auto" });
    },
    [closeMenu],
  );

  return (
    <>
      <button
        aria-controls="prelaunch-mobile-menu"
        aria-expanded={isMenuOpen}
        aria-label={isMenuOpen ? ariaCloseMenu : ariaToggleMenu}
        className="-mr-2 inline-flex h-11 w-11 items-center justify-center lg:hidden"
        onClick={() => setIsMenuOpen((open) => !open)}
        type="button"
      >
        <span className="relative block h-4 w-7">
          {/*
            Hamburger bars animate `backgroundColor` per-frame, so we hand
            Framer Motion two literal hex stops it can interpolate. Both
            map to canonical brand tokens:
              `#F3F0E8` ≡ champagne-white (matches `bg-champagne-white`)
              `#645A56` ≡ mocha @ ~65% over champagne-white background
                          (visual peer of `text-muted-foreground`)
            CSS-variable tokens with alpha cannot be used here because
            Motion needs concrete colors on both ends of the tween.
          */}
          <motion.span
            animate={{
              backgroundColor: isMenuOpen ? "#F3F0E8" : "#645A56",
              rotate: isMenuOpen ? 45 : 0,
              y: isMenuOpen ? 7 : 0,
            }}
            className="absolute top-0 left-0 block h-[2px] w-full rounded-full"
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
          <motion.span
            animate={{
              backgroundColor: isMenuOpen ? "#F3F0E8" : "#645A56",
              opacity: isMenuOpen ? 0 : 1,
            }}
            className="absolute top-[7px] left-0 block h-[2px] w-full rounded-full"
            transition={{ duration: 0.15, ease: "easeOut" }}
          />
          <motion.span
            animate={{
              backgroundColor: isMenuOpen ? "#F3F0E8" : "#645A56",
              rotate: isMenuOpen ? -45 : 0,
              y: isMenuOpen ? -7 : 0,
            }}
            className="absolute top-[14px] left-0 block h-[2px] w-full rounded-full"
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </span>
      </button>

      {isMounted &&
        createPortal(
          <div data-prelaunch>
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  animate={{ opacity: 1 }}
                  aria-modal="true"
                  className="pt-safe bg-mocha fixed inset-0 z-[60] flex flex-col lg:hidden"
                  exit={{ opacity: 0 }}
                  id="prelaunch-mobile-menu"
                  initial={{ opacity: 0 }}
                  role="dialog"
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div
                    aria-hidden="true"
                    className="h-[calc(3.5rem+var(--announcement-bar-offset,0px))] shrink-0"
                  />

                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <nav className="section-container mt-6">
                      <ul className="flex flex-col">
                        {navLinks.map((link) => (
                          <li key={link.href}>
                            <a
                              className="type-heading-xl text-champagne-white border-champagne-white/10 block border-b py-4"
                              href={link.href}
                              onClick={(e) => handleMobileNavClick(e, link.href)}
                            >
                              {link.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>

                  <div className="border-champagne-white/10 border-t">
                    <div className="section-container pt-6 [padding-bottom:calc(env(safe-area-inset-bottom)+1.5rem)]">
                      <div className="flex flex-col gap-3">
                        <Link
                          className="prelaunch-cta inline-flex w-full items-center justify-center"
                          href="https://info.ageless.ai/live-product-demo"
                          onClick={closeMenu}
                        >
                          <span className="prelaunch-cta-label type-body-sm whitespace-nowrap">
                            {bookDemoLabel}
                          </span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>,
          document.body,
        )}
    </>
  );
}
